import React, { useCallback, useEffect, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { analyticsService } from '../../api/index';
import {
  AlertTriangle,
  Award,
  BarChart2,
  BookOpen,
  Clock,
  Download,
  RefreshCw,
  TrendingUp,
  Users,
} from 'lucide-react';

// ─── Colour palette ───────────────────────────────────────────────────────────
const COLORS = ['#2F7C7B', '#10B981', '#F59E0B', '#2FA7A1', '#F2B542', '#0F1F23'];

// ─── Sparkline ───────────────────────────────────────────────────────────────
function Sparkline({ data, valueKey, color = '#2F7C7B', height = 60 }) {
  if (!data || data.length === 0) return <div style={{ height }} />;
  const vals = data.map((d) => d[valueKey] || 0);
  const max = Math.max(...vals, 1);
  const w = 300;
  const h = height;
  const pts = vals.map((v, i) => `${(i / Math.max(vals.length - 1, 1)) * w},${h - (v / max) * h}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={`url(#grad-${color.replace('#', '')})`} />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {/* Last point dot */}
      {vals.length > 0 && (
        <circle
          cx={(vals.length - 1) / Math.max(vals.length - 1, 1) * w}
          cy={h - (vals[vals.length - 1] / max) * h}
          r="4" fill={color}
        />
      )}
    </svg>
  );
}

// ─── Gauge (seat utilisation) ─────────────────────────────────────────────────
function Gauge({ pct, label, color = '#2F7C7B', size = 120 }) {
  const r = size / 2 - 10;
  const circ = Math.PI * r; // semi-circle
  const dash = (pct / 100) * circ;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={size / 2 + 20} viewBox={`0 0 ${size} ${size / 2 + 20}`}>
        <path d={`M 10 ${size / 2} A ${r} ${r} 0 0 1 ${size - 10} ${size / 2}`}
          fill="none" stroke="var(--color-border)" strokeWidth="10" strokeLinecap="round" />
        <path d={`M 10 ${size / 2} A ${r} ${r} 0 0 1 ${size - 10} ${size / 2}`}
          fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          style={{ transition: 'stroke-dasharray 1s ease' }}
        />
        <text x="50%" y={size / 2 + 18} textAnchor="middle" fontSize="16" fontWeight="800" fill="var(--color-text-primary)">
          {pct}%
        </text>
      </svg>
      <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

// ─── Bar chart for grade distribution ────────────────────────────────────────
function GradeBar({ label, count, max, color }) {
  const pct = max > 0 ? (count / max) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <div style={{ width: 80, fontSize: 12, color: 'var(--color-text-secondary)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 14, background: 'var(--color-border)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 99, transition: 'width 0.8s ease' }} />
      </div>
      <div style={{ width: 30, textAlign: 'right', fontSize: 12, fontWeight: 700, color: 'var(--color-text-primary)' }}>{count}</div>
    </div>
  );
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────
function KPICard({ icon: Icon, label, value, sub, color }) {
  return (
    <div style={{
      background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)',
      borderRadius: 16,
      padding: '22px 20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -16, right: -16, width: 72, height: 72, borderRadius: '50%', background: `${color}18` }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{ background: `${color}22`, borderRadius: 10, padding: 8, display: 'flex' }}>
          <Icon size={18} style={{ color }} />
        </div>
        <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{label}</span>
      </div>
      <div style={{ fontSize: 30, fontWeight: 900, color: 'var(--color-text-primary)', lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 5, lineHeight: 1.4 }}>{sub}</div>}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReportingCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await analyticsService.getAdminSummary();
      setData(res.data);
    } catch (e) {
      setError('Failed to load reporting data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (report) => {
    try {
      setExportLoading(report);
      const res = await analyticsService.exportReport(report);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${report}_report.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Export failed', e);
    } finally {
      setExportLoading('');
    }
  };

  const gradeColors = { 'A (90-100)': '#10b981', 'B (75-89)': '#2F7C7B', 'C (60-74)': '#f59e0b', 'D (50-59)': '#F2B542', 'F (<50)': '#ef4444' };
  const totalLearningHours = Number(data?.learning_hours?.total) || 0;
  const learningHoursValue = totalLearningHours > 0
    ? `${totalLearningHours.toLocaleString('en-ZA')} ${totalLearningHours === 1 ? 'hour' : 'hours'}`
    : '—';

  return (
    <DashboardLayout>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, color: 'var(--color-text-primary)' }}>
              Reporting Center
            </h1>
            <p style={{ margin: '6px 0 0', color: 'var(--color-text-secondary)', fontSize: 14 }}>
              Organisation-wide learning analytics and usage reports.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {['enrollments', 'learning_hours', 'grades', 'integrity'].map((r) => (
              <button
                key={r}
                id={`export-btn-${r}`}
                onClick={() => handleExport(r)}
                disabled={!!exportLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: '1px solid var(--color-border)',
                  background: exportLoading === r ? 'var(--color-bg-secondary)' : 'transparent',
                  color: 'var(--color-text-primary)',
                  transition: 'all 0.2s',
                }}
              >
                <Download size={14} />
                {r.replace('_', ' ')}
              </button>
            ))}
            <button
              id="refresh-reports-btn"
              onClick={fetchData}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--color-text-primary)',
              }}
            >
              <RefreshCw size={14} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background: '#ef444422', border: '1px solid #ef4444', borderRadius: 12, padding: '14px 18px', marginBottom: 24, color: '#ef4444', fontSize: 14 }}>
            {error}
          </div>
        )}

        {loading && !data && (
          <div style={{ textAlign: 'center', padding: 80, color: 'var(--color-text-secondary)' }}>
            Loading reporting data…
          </div>
        )}

        {data && (
          <>
            {/* ── KPI Row ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 28 }}>
              <KPICard icon={Users} label="Total Enrolled" value={data.enrollment_funnel.total} color="#2F7C7B" />
              <KPICard icon={TrendingUp} label="Active Learners" value={data.enrollment_funnel.active} color="#10b981" />
              <KPICard icon={BookOpen} label="Completions" value={data.enrollment_funnel.completed} color="#f59e0b" />
              <KPICard icon={Award} label="Certs Issued" value={data.enrollment_funnel.certificates_issued} color="#2FA7A1" />
              <KPICard
                icon={Clock}
                label="Total Learning Hours"
                value={learningHoursValue}
                sub={totalLearningHours > 0 ? 'Across all recorded learning activity' : 'No learning hours recorded yet'}
                color="#2FA7A1"
              />
            </div>

            {/* ── Row 2: Seat Utilisation + Enrolment Trend ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 20, marginBottom: 20 }}>

              {/* Seat Utilisation */}
              <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 20, color: 'var(--color-text-primary)' }}>
                  Seat Utilisation
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
                  <Gauge pct={data.seat_utilisation.overall_utilisation_percent} label={`${data.seat_utilisation.total_enrolled} / ${data.seat_utilisation.total_seats} seats`} color="#2F7C7B" />
                </div>
                {data.seat_utilisation.by_campus.map((c, i) => (
                  <div key={c.campus_id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>{c.campus_name}</span>
                      <span style={{ color: 'var(--color-text-secondary)' }}>{c.enrolled_students}/{c.max_seats}</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 99, overflow: 'hidden' }}>
                      <div style={{
                        width: `${c.utilisation_percent}%`, height: '100%',
                        background: COLORS[i % COLORS.length],
                        borderRadius: 99, transition: 'width 0.8s ease',
                      }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Enrolment Trend */}
              <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: 'var(--color-text-primary)' }}>
                  Monthly Enrolments (last 6 months)
                </div>
                {data.enrollment_funnel.trend.length > 0 ? (
                  <>
                    <Sparkline data={data.enrollment_funnel.trend} valueKey="count" color="#2F7C7B" height={90} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{data.enrollment_funnel.trend[0]?.month}</span>
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{data.enrollment_funnel.trend[data.enrollment_funnel.trend.length - 1]?.month}</span>
                    </div>
                  </>
                ) : (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>No enrolment data yet.</p>
                )}

                {/* Learning hours trend */}
                <div style={{ fontWeight: 700, fontSize: 15, marginTop: 20, marginBottom: 6, color: 'var(--color-text-primary)' }}>
                  Monthly Learning Hours
                </div>
                {data.learning_hours.trend.length > 0 ? (
                  <Sparkline data={data.learning_hours.trend} valueKey="hours" color="#10b981" height={70} />
                ) : (
                  <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>No hours data yet.</p>
                )}
              </div>
            </div>

            {/* ── Row 3: Top Courses + Grade Distribution ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20, marginBottom: 20 }}>

              {/* Top Courses */}
              <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BarChart2 size={16} /> Top Courses by Enrolment
                </div>
                {data.top_courses.length === 0 && <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>No course data yet.</p>}
                {data.top_courses.map((c, i) => {
                  const maxEnr = data.top_courses[0]?.enrollments || 1;
                  return (
                    <div key={c.course_id} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span style={{ color: 'var(--color-text-primary)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '65%' }}>
                          {i + 1}. {c.course_title}
                        </span>
                        <span style={{ color: 'var(--color-text-secondary)', flexShrink: 0 }}>
                          {c.enrollments} enrolled · {c.completion_rate}% done
                        </span>
                      </div>
                      <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 99, overflow: 'hidden' }}>
                        <div style={{
                          width: `${(c.enrollments / maxEnr) * 100}%`, height: '100%',
                          background: COLORS[i % COLORS.length],
                          borderRadius: 99,
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Grade Distribution */}
              <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24 }}>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--color-text-primary)' }}>
                  Grades Distribution
                </div>
                {(() => {
                  const entries = Object.entries(data.grades_distribution);
                  const max = Math.max(...entries.map(([, v]) => v), 1);
                  return entries.map(([label, count]) => (
                    <GradeBar key={label} label={label} count={count} max={max} color={gradeColors[label] || '#2F7C7B'} />
                  ));
                })()}
              </div>
            </div>

            {/* ── Integrity Alerts ── */}
            <div style={{ background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 16, padding: 24 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertTriangle size={16} style={{ color: '#ef4444' }} />
                Academic Integrity Alerts
                {data.integrity_alerts.length > 0 && (
                  <span style={{ background: '#ef444422', color: '#ef4444', borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                    {data.integrity_alerts.length}
                  </span>
                )}
              </div>
              {data.integrity_alerts.length === 0 ? (
                <p style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>✓ No plagiarism flags detected.</p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                        {['Student', 'Assessment', 'Course', 'Plagiarism Score', 'Date'].map((h) => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.integrity_alerts.map((a) => (
                        <tr key={a.submission_id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td style={{ padding: '10px 10px' }}>{a.student_name}</td>
                          <td style={{ padding: '10px 10px' }}>{a.assessment_title}</td>
                          <td style={{ padding: '10px 10px', color: 'var(--color-text-secondary)' }}>{a.course_title}</td>
                          <td style={{ padding: '10px 10px' }}>
                            <span style={{
                              background: '#ef444422', color: '#ef4444',
                              borderRadius: 6, padding: '2px 8px', fontWeight: 700,
                            }}>
                              {(a.plagiarism_score * 100).toFixed(0)}%
                            </span>
                          </td>
                          <td style={{ padding: '10px 10px', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                            {a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </DashboardLayout>
  );
}
